<template>
  <ValidationObserver v-slot="{ handleSubmit }">
    <div class="absolute inset-0 flex flex-col overflow-y-hidden">
      <div class="flex-1 overflow-y-auto">
        <header class="space-y-1 py-6 px-4 bg-cyan-700">
          <div class="flex items-center justify-between">
            <h2 class="text-lg leading-7 font-medium text-white">
              {{
                formAction === 'edit' ? 'แก้ไขเฟิร์มแวร์' : 'เพิ่มเฟิร์มแวร์'
              }}
            </h2>
            <button
              @click="onClose"
              aria-label="Close panel"
              class="
                text-cyan-200
                hover:text-white
                transition
                ease-in-out
                duration-150
              "
            >
              <svg
                class="h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </div>
          <div>
            <p class="text-sm leading-5 text-cyan-300">
              {{
                formAction === 'edit' ? 'แก้ไขเฟิร์มแวร์' : 'เพิ่มเฟิร์มแวร์'
              }}
              เพื่อใช้ในการอัพเดทเฟิร์มแวร์ของอุปกรณ์
            </p>
          </div>
        </header>

        <!-- Error Message -->
        <div
          v-if="errors.length > 0"
          class="px-4 py-3 bg-red-600 bg-opacity-20 flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 text-red-600"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"
            />
          </svg>
          <p class="text-base font-semibold text-red-800 flex-1 px-3">
            {{ errors[0] }}
          </p>
          <button @click="errors = []" type="button" class="focus:outline-none">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-red-600"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>
        <!-- End Error Message -->

        <div class="p-4 space-y-6">
          <div>
            <label class="block text-base font-medium text-gray-700">
              เลือก Device Model
              <span class="text-xl text-red-600">*</span>
            </label>
            <ValidationProvider
              name="Device Model"
              rules="required"
              v-slot="{ errors }"
            >
              <AppAutoComplete
                v-model="firmware.hardware_model"
                :isSearch="true"
                :options="hardwareModels"
                label="เลือก Device Model"
                item-text-key="title"
                item-value-key="hardware_model_code"
                class="mt-1"
                :isDisabled="formAction === 'edit'"
              />
              <span class="text-base text-red-600">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div>
            <ValidationProvider
              name="เวอร์ชั่น"
              rules="required"
              v-slot="{ errors }"
            >
              <AppInput
                v-model.trim="firmware.firmware_version"
                label="เวอร์ชั่น"
                required
                type="text"
              />
              <span class="text-base text-red-600">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div>
            <AppTextarea v-model="firmware.description" label="คำอธิบาย" />
          </div>

          <div>
            <label class="block text-base font-medium text-gray-700"
              >ไฟล์เฟิร์มแวร์ <span class="text-xl text-red-600">*</span>
            </label>
            <ValidationProvider
              name="ไฟล์เฟิร์มแวร์"
              rules="required"
              ref="provider"
              v-slot="{ errors }"
            >
              <input
                class="
                  mt-1
                  border
                  block
                  w-full
                  shadow-sm
                  text-base
                  border-gray-300
                  rounded-md
                  p-1
                  focus:outline-none
                  focus:ring-1
                  focus:ring-cyan-500
                  focus:border-cyan-500
                  text-red-600
                "
                type="file"
                accept=".bin, .tar"
                @change="onFileSelected"
              />
              <span class="text-base text-red-600">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div>
            <label class="block text-base font-medium text-gray-700">
              ใช้ได้เฉพาะบริษัท
              <span class="text-sm text-gray-400">(ไม่บังคับ)</span>
            </label>
            <AppAutoComplete
              v-model="firmware.company"
              :isSearch="true"
              :options="customers"
              position="top"
              label="เลือกบริษัท"
              item-text-key="title_th"
              item-value-key="company_uuid"
              class="mt-1"
            />
          </div>
        </div>
      </div>

      <div>
        <div class="p-4 border-t flex justify-end">
          <div v-if="!isDataChange && formAction === 'edit'">
            <button
              @click="onDelete"
              :disabled="loading"
              :class="{ 'cursor-not-allowed': loading }"
              type="button"
              class="
                w-full
                inline-flex
                justify-center
                rounded-md
                border border-transparent
                shadow-sm
                px-4
                py-2
                bg-red-600
                text-base
                font-medium
                text-white
                hover:bg-red-700
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-red-500
                sm:w-auto
              "
            >
              <svg
                v-if="loading"
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              <span class="ml-2">ลบ</span>
            </button>
          </div>

          <div v-else class="space-x-4">
            <button
              @click="onCancel"
              :disabled="loading"
              :class="{ 'cursor-not-allowed': loading }"
              type="button"
              class="
                w-full
                inline-flex
                justify-center
                rounded-md
                border border-gray-300
                shadow-sm
                px-4
                py-2
                bg-white
                text-base
                font-medium
                text-gray-700
                hover:bg-gray-50
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-cyan-500
                sm:ml-3 sm:w-auto
              "
            >
              ยกเลิก
            </button>

            <button
              v-if="formAction === 'add'"
              @click="handleSubmit(onSubmit)"
              :disabled="loading"
              :class="{ 'cursor-not-allowed': loading }"
              type="button"
              class="
                w-full
                inline-flex
                justify-center
                rounded-md
                border border-transparent
                shadow-sm
                px-4
                py-2
                bg-cyan-600
                text-base
                font-medium
                text-white
                hover:bg-cyan-700
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-cyan-500
                sm:w-auto
              "
            >
              <svg
                v-if="loading"
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              ตกลง
            </button>

            <button
              v-if="formAction === 'edit'"
              @click="handleSubmit(onSave)"
              :disabled="loading"
              :class="{ 'cursor-not-allowed': loading }"
              type="button"
              class="
                w-full
                inline-flex
                justify-center
                rounded-md
                border border-transparent
                shadow-sm
                px-4
                py-2
                bg-cyan-600
                text-base
                font-medium
                text-white
                hover:bg-cyan-700
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-cyan-500
                sm:w-auto
              "
            >
              <svg
                v-if="loading"
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              บันทึก
            </button>
          </div>
        </div>
      </div>
    </div>
  </ValidationObserver>
</template>

<script>
import AppInput from '@/components/AppInput'
import AppTextarea from '@/components/AppTextarea'
import AppAutoComplete from '@/components/AppAutoComplete'
import { extend } from 'vee-validate'
import { required } from 'vee-validate/dist/rules'

// No message specified.
// extend('required', required)

// Override the default message.
extend('required', {
  ...required,
  message: 'จำเป็นต้องระบุ {_field_}',
})

export default {
  name: 'FormFirmware',

  props: {
    formAction: {
      type: String,
    },
    initFirmware: {
      type: Object,
    },
    firmware: {
      type: Object,
    },
  },

  components: {
    AppInput,
    AppTextarea,
    AppAutoComplete,
  },

  data() {
    return {
      loading: false,
      errors: [],
      selectedFile: null,
    }
  },

  computed: {
    isDataChange() {
      if (
        this.initFirmware.firmware_version !== this.firmware.firmware_version ||
        this.initFirmware.description !== this.firmware.description ||
        this.initFirmware.company?.company_uuid !==
          this.firmware.company?.company_uuid ||
        this.selectedFile
      ) {
        this.$emit('isDataChange', true)
        return true
      }
      this.$emit('isDataChange', false)
      return false
    },

    hardwareModels() {
      return this.$store.state.hardware.hardwareModels
    },

    customers() {
      return this.$store.state.customer.customers
    },
  },

  methods: {
    async onFileSelected(event) {
      const { valid } = await this.$refs.provider.validate(event)
      if (!valid) return
      this.selectedFile = event.target.files[0]
    },
    onCancel() {
      this.$emit('onCancel')
    },

    onClose() {
      this.$emit('onClose')
    },

    getBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = (error) => reject(error)
      })
    },

    async onSubmit() {
      try {
        this.loading = true

        let file_base64 = null
        let file_ext = null
        let file_name = null

        if (this.selectedFile) {
          const fileType = this.selectedFile.name.split('.').pop()

          if (
            fileType.toLowerCase() !== 'bin' &&
            fileType.toLowerCase() !== 'tar'
          ) {
            this.errors.push('Invalid file type. (bin, tar)')
            return
          }

          const result = await this.getBase64(this.selectedFile)

          file_base64 = result.split(',').pop()
          file_ext = `.${fileType}`

          const arr = this.selectedFile.name.split('.')
          arr.pop()
          file_name = arr.join('.')
        }

        const firmware = {
          company_uuid: this.firmware.company?.company_uuid,
          hardware_model_code:
            this.firmware.hardware_model?.hardware_model_code,
          firmware_version: this.firmware.firmware_version,
          description: this.firmware.description,
          file_name: file_name,
          file_ext: file_ext,
          file_base64: file_base64,
          file_size_byte: this.selectedFile.size,
        }

        const payload = {
          firmware,
        }

        await this.$store.dispatch('firmware/addFirmware', payload)

        const notification = {
          type: 'success',
          message: 'Your firmware has been uploaded!',
        }
        this.$store.dispatch('notification/add', notification)

        this.$emit('isDataChange', false)
        this.onClose()
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors.push(
            error.response.data.message || 'Something went wrong.'
          )
        }

        this.$emit('isDataChange', false)
        console.log(error.response)
      } finally {
        this.loading = false
      }
    },

    async onSave() {
      try {
        this.loading = true

        let file_base64 = null
        let file_ext = null
        let file_name = null
        let file_size_byte = null

        if (this.selectedFile) {
          const fileType = this.selectedFile.name.split('.').pop()

          if (
            fileType.toLowerCase() !== 'bin' &&
            fileType.toLowerCase() !== 'tar'
          ) {
            this.errors.push('Invalid file type. (bin, tar)')
            return
          }

          const result = await this.getBase64(this.selectedFile)

          file_base64 = result.split(',').pop()
          file_ext = `.${fileType}`

          const arr = this.selectedFile.name.split('.')
          arr.pop()
          file_name = arr.join('.')
          file_size_byte = this.selectedFile.size
        }

        const firmware = {
          company_uuid: this.firmware.company?.company_uuid,
          firmware_uuid: this.firmware.firmware_uuid,
          hardware_model_code:
            this.firmware.hardware_model?.hardware_model_code,
          firmware_version: this.firmware.firmware_version,
          description: this.firmware.description,
          file_name: file_name,
          file_ext: file_ext,
          file_base64: file_base64,
          file_size_byte: file_size_byte,
        }

        const payload = {
          firmware_uuid: this.firmware.firmware_uuid,
          body: firmware,
        }

        await this.$store.dispatch('firmware/editFirmware', payload)

        const notification = {
          type: 'success',
          message: 'Your firmware has been updated!',
        }
        this.$store.dispatch('notification/add', notification)

        this.$emit('isDataChange', false)
        this.onClose()
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors.push(
            error.response.data.message || 'Something went wrong.'
          )
        }

        this.$emit('isDataChange', false)
        console.log(error.response)
      } finally {
        this.loading = false
      }
    },

    onDelete() {
      this.$emit('onDelete', this.firmware)
    },
  },
}
</script>
